import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Skeleton,
  useColorModeValue,
  Flex,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Heading,
  IconButton,
  Text,
  VStack,
  Input,
  HStack,
  Button,
  Select,
  FormControl,
  FormLabel,
  Collapse,
  Badge,
  SimpleGrid,
} from "@chakra-ui/react";
import React, { useEffect, useMemo, useState } from "react";
import { FiFilter, FiSearch, FiX, FiChevronDown, FiChevronRight } from "react-icons/fi";
import ListOfPayrol from "./ListOfPayrol";

// Helper function to format hours (convert "00:00" to "0h", "160:00" to "160h")
const formatHours = (hoursString) => {
  if (!hoursString || hoursString === "00:00" || hoursString === "0") return "0h";
  if (hoursString.includes(":")) {
    const [hours] = hoursString.split(":");
    const hoursNum = parseInt(hours);
    return hoursNum > 0 ? `${hoursNum}h` : "0h";
  }
  const num = parseInt(hoursString);
  return isNaN(num) ? hoursString : `${num}h`;
};

// Helper function to format currency
const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "" || value === "0" || value === "0.00" || value === 0) return "₦0";
  
  // Parse the value (handle both string and number)
  let numValue;
  if (typeof value === 'string') {
    numValue = parseFloat(value.replace(/,/g, ''));
  } else {
    numValue = parseFloat(value);
  }
  
  if (isNaN(numValue)) return typeof value === 'string' ? value : "₦0";
  
  // Show decimals only if not a whole number
  if (numValue % 1 === 0) {
    return `₦${numValue.toLocaleString('en-US')}`;
  }
  return `₦${numValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const PayrollTable = ({ employeeData = [], loading }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const bgColor = useColorModeValue("white", "gray.800");
  const [total, setTotal] = useState();
  const [data, setData] = useState();
  const [searchTerm, setSearchTerm] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [branchFilter, setBranchFilter] = useState("");
  const [salaryRangeFilter, setSalaryRangeFilter] = useState("");
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [collapsedSections, setCollapsedSections] = useState({
    hours: true,
    regularPay: true,
    totalPay: true,
    earnings: true,
  });

  useEffect(() => {
    // Handle total calculations
  }, []);

  const handleSort = useMemo(() => {
    const d = employeeData && employeeData?.filter((t) => !t?.fullName.includes("Total"));
    const t = employeeData && employeeData?.filter((t) => t?.fullName.includes("Total"));
    setTotal(t);
    return d;
  }, [employeeData]);

  // Get total summary row
  const totalSummary = useMemo(() => {
    return employeeData && employeeData?.find((t) => t?.fullName.includes("Total"));
  }, [employeeData]);

  // Filter data based on search and filters
  const filteredData = useMemo(() => {
    let filtered = handleSort || [];
    
    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(item => 
        item?.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item?.employee_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item?.department?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item?.branch_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Department filter
    if (departmentFilter) {
      filtered = filtered.filter(item => 
        item?.department === departmentFilter
      );
    }
    
    // Branch filter
    if (branchFilter) {
      filtered = filtered.filter(item => 
        item?.branch_name === branchFilter
      );
    }
    
    // Salary range filter
    if (salaryRangeFilter) {
      const [min, max] = salaryRangeFilter.split('-').map(Number);
      filtered = filtered.filter(item => {
        const totalPay = item?.totalPay || 0;
        return totalPay >= min && totalPay <= max;
      });
    }
    
    return filtered;
  }, [handleSort, searchTerm, departmentFilter, salaryRangeFilter]);

  // Get unique departments for filter dropdown
  const departments = useMemo(() => {
    if (!handleSort) return [];
    const depts = [...new Set(handleSort.map(item => item?.department).filter(Boolean))];
    return depts.sort();
  }, [handleSort]);

  // Get unique branches for filter dropdown
  const branches = useMemo(() => {
    if (!handleSort) return [];
    const branchList = [...new Set(handleSort.map(item => item?.branch_name).filter(Boolean))];
    return branchList.sort();
  }, [handleSort]);

  const clearFilters = () => {
    setSearchTerm("");
    setDepartmentFilter("");
    setBranchFilter("");
    setSalaryRangeFilter("");
  };

  const toggleRowExpansion = (employeeId) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(employeeId)) {
      newExpandedRows.delete(employeeId);
    } else {
      newExpandedRows.add(employeeId);
    }
    setExpandedRows(newExpandedRows);
  };

  if (loading) {
    return (
      <Box p={6}>
        <TableContainer>
          <Table variant="simple">
            <Thead>
              <Tr>
                <Th></Th>
                <Th>Name</Th>
                <Th>ID</Th>
                <Th>Department</Th>
                <Th>Hourly Rate</Th>
                <Th>Hours Worked</Th>
                <Th>Overtime</Th>
                <Th>Regular Pay</Th>
                <Th>Total</Th>
              </Tr>
            </Thead>
            <Tbody>
              {[1, 2, 3, 4, 5].map((i) => (
                <Tr key={i}>
                  <Td><Skeleton height="20px" /></Td>
                  <Td><Skeleton height="20px" /></Td>
                  <Td><Skeleton height="20px" /></Td>
                  <Td><Skeleton height="20px" /></Td>
                  <Td><Skeleton height="20px" /></Td>
                  <Td><Skeleton height="20px" /></Td>
                  <Td><Skeleton height="20px" /></Td>
                  <Td><Skeleton height="20px" /></Td>
                  <Td><Skeleton height="20px" /></Td>
                  <Td><Skeleton height="20px" /></Td>
                  <Td><Skeleton height="20px" /></Td>
                </Tr>
              ))}
              {/* Total Summary Skeleton */}
              <Tr bg="blue.50">
                <Td><Skeleton height="20px" /></Td>
                <Td><Skeleton height="20px" /></Td>
                <Td><Skeleton height="20px" /></Td>
                <Td><Skeleton height="20px" /></Td>
                <Td><Skeleton height="20px" /></Td>
                <Td><Skeleton height="20px" /></Td>
                <Td><Skeleton height="20px" /></Td>
                <Td><Skeleton height="20px" /></Td>
                <Td><Skeleton height="20px" /></Td>
                <Td><Skeleton height="20px" /></Td>
                <Td><Skeleton height="20px" /></Td>
              </Tr>
            </Tbody>
          </Table>
        </TableContainer>
      </Box>
    );
  }

  if (!loading && !employeeData?.length) {
    return (
      <Box p={8} textAlign="center">
        <VStack spacing={4}>
          <Box
            p={4}
            borderRadius="50%"
            bg="gray.100"
            color="gray.500"
            fontSize="2xl"
          >
            💰
          </Box>
          <VStack spacing={2}>
            <Text fontSize="lg" fontWeight="600" color="gray.700">
              No Payroll Data
            </Text>
            <Text color="gray.500" fontSize="sm">
              No payroll records found for the selected period
            </Text>
          </VStack>
        </VStack>
      </Box>
    );
  }

  return (
    <Box>
      {/* Compact Header with Search */}
      <Flex 
        justifyContent="space-between" 
        alignItems="center" 
        p={4} 
        borderBottom="1px solid" 
        borderColor="gray.200"
        bg={bgColor}
        gap={4}
      >
        <HStack spacing={3} flex={1}>
          <Heading size="md" color="gray.900" fontWeight="600">
            Employee Payroll
          </Heading>
          <Text color="gray.600" fontSize="sm">
            ({filteredData?.length || 0} of {handleSort?.length || 0} employees)
          </Text>
        </HStack>
        <HStack spacing={2} flex={1} maxW="400px">
          <Box position="relative" flex={1}>
            <Input
              placeholder="Search by name, ID, or department..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              pl={10}
              pr={searchTerm ? 10 : 4}
              size="md"
              borderRadius="8px"
              borderColor="gray.200"
              _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)" }}
            />
            <Box position="absolute" left={3} top="50%" transform="translateY(-50%)" color="gray.400">
              <FiSearch size={16} />
            </Box>
            {searchTerm && (
              <Box 
                position="absolute" 
                right={3} 
                top="50%" 
                transform="translateY(-50%)" 
                cursor="pointer"
                onClick={() => setSearchTerm("")}
                color="gray.400"
                _hover={{ color: "gray.600" }}
              >
                <FiX size={16} />
              </Box>
            )}
          </Box>
          {(searchTerm || departmentFilter || salaryRangeFilter) && (
            <Button
              size="md"
              variant="ghost"
              onClick={clearFilters}
              leftIcon={<FiX />}
              color="gray.600"
            >
              Clear
            </Button>
          )}
          <IconButton
            icon={<FiFilter />}
            variant="ghost"
            onClick={onOpen}
            aria-label="Filter payroll"
            color="gray.600"
            size="md"
            _hover={{ bg: "gray.100" }}
          />
        </HStack>
      </Flex>

      {/* Payroll Table */}
      <TableContainer>
        <Table variant="simple">
          <Thead>
            <Tr>
              <Th></Th>
              <Th>Employee</Th>
              <Th>Dept</Th>
              <Th>Branch</Th>
              <Th>Regular Pay Gross</Th>
              <Th>Total Pay Gross</Th>
              <Th>Regular Tax</Th>
              <Th>Total Tax</Th>
              <Th>Deductions</Th>
              <Th>Total Net Pay</Th>
              <Th>Regular Net Salary</Th>
            </Tr>
          </Thead>
                     <Tbody>
             {filteredData?.map((item, index) => (
               <React.Fragment key={index}>
                 <ListOfPayrol 
                   {...item} 
                   isExpanded={expandedRows.has(item.employee_id)}
                   onToggle={() => toggleRowExpansion(item.employee_id)}
                 />
                 <Tr>
                   <Td colSpan={11} p={0}>
                     <Collapse in={expandedRows.has(item.employee_id)} animateOpacity>
                       <Box bg="gray.50" p={4} borderTop="1px solid" borderColor="gray.200">
                         <VStack spacing={4} align="stretch">
                           <Heading size="sm" color="gray.700" mb={2}>
                             Detailed Payroll Information
                           </Heading>
                           
                           {/* Hours & Rate Information */}
                           <Box>
                             <Text fontSize="xs" color="gray.500" fontWeight="600" mb={2} textTransform="uppercase">
                               Hours & Rate
                             </Text>
                             <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={3}>
                               <DetailItem label="Hourly Rate" value={item.hourlySalary} />
                               <DetailItem label="Hours Worked" value={item.totalHoursWorked} />
                               <DetailItem label="Overtime Hours" value={item.overtimeHours} />
                               <DetailItem label="Missed Hours" value={item.missedHours} />
                             </SimpleGrid>
                           </Box>

                           {/* Regular Pay Breakdown */}
                           <Box borderTop="1px solid" borderColor="gray.200" pt={4}>
                             <Text fontSize="xs" color="gray.500" fontWeight="600" mb={2} textTransform="uppercase">
                               Regular Pay Breakdown
                             </Text>
                             <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={3}>
                               <DetailItem label="Regular Pay Gross" value={item.regularPayGross} />
                               <DetailItem label="Regular Pay Tax" value={item.regularPayTax} isNegative={true} />
                               <DetailItem label="Regular Pay Net" value={item.regularPayNet} />
                               <DetailItem label="Regular Net Salary" value={item.regularNetSalary} />
                             </SimpleGrid>
                           </Box>

                           {/* Total Pay Breakdown */}
                           <Box borderTop="1px solid" borderColor="gray.200" pt={4}>
                             <Text fontSize="xs" color="gray.500" fontWeight="600" mb={2} textTransform="uppercase">
                               Total Pay Breakdown
                             </Text>
                             <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={3}>
                               <DetailItem label="Total Pay Gross" value={item.totalPayGross} />
                               <DetailItem label="Total Pay Tax" value={item.totalPayTax} isNegative={true} />
                               <DetailItem label="Total Pay Net" value={item.totalPayNet} />
                             </SimpleGrid>
                           </Box>

                           {/* Earnings & Deductions */}
                           <Box borderTop="1px solid" borderColor="gray.200" pt={4}>
                             <Text fontSize="xs" color="gray.500" fontWeight="600" mb={2} textTransform="uppercase">
                               Earnings & Deductions
                             </Text>
                             <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={3}>
                               <DetailItem label="Overtime Pay" value={item.overtimePay} />
                               <DetailItem label="Leave Allowance" value={item.leaveAllowance} />
                               <DetailItem label="Bonus" value={item.bonus || item.thirteenthMonthBonus} />
                               <DetailItem label="Missed Hours Deduction" value={item.missedHoursDeduction} isNegative={true} />
                             </SimpleGrid>
                          <SimpleGrid columns={{ base: 1, md: 2, lg: 2 }} spacing={3} mt={3}>
                            <DetailItem label="Other Deductions" value={item.deductions} isNegative={true} />
                            <DetailItem 
                              label="Total Deductions" 
                              value={(() => {
                                const deductions = parseFloat(String(item.deductions || 0).replace(/,/g, '')) || 0;
                                const missedHours = parseFloat(String(item.missedHoursDeduction || 0).replace(/,/g, '')) || 0;
                                return deductions + missedHours;
                              })()} 
                              isNegative={true} 
                            />
                          </SimpleGrid>
                           </Box>
                           
                           {/* Net Pay Summary */}
                           <Box borderTop="2px solid" borderColor="green.400" pt={4}>
                             <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                               <DetailItem 
                                 label="Total Net Pay" 
                                 value={item.totalPayNet} 
                                 isNetSalary={true} 
                               />
                               <DetailItem 
                                 label="Regular Net Salary" 
                                 value={item.regularNetSalary} 
                                 isNetSalary={true} 
                               />
                             </SimpleGrid>
                           </Box>
                         </VStack>
                       </Box>
                     </Collapse>
                   </Td>
                 </Tr>
               </React.Fragment>
             ))}
             
             {/* Total Summary Row */}
             {totalSummary && (
               <React.Fragment>
                 <Tr bg="blue.50" _hover={{ bg: "blue.100" }}>
                   <Td>
                     <IconButton
                       icon={<FiChevronDown />}
                       variant="ghost"
                       size="sm"
                       aria-label="Total Summary"
                       isDisabled
                     />
                   </Td>
                   <Td fontWeight="700" color="blue.700">Total Summary</Td>
                   <Td fontWeight="600" color="blue.700">—</Td>
                   <Td fontWeight="600" color="blue.700">{totalSummary.branch_name || "All Branches"}</Td>
                   <Td fontWeight="600" color="blue.700">{formatCurrency(totalSummary.regularPayGross)}</Td>
                   <Td fontWeight="700" color="blue.700">{formatCurrency(totalSummary.totalPayGross)}</Td>
                   <Td fontWeight="600" color="red.500">{formatCurrency(totalSummary.regularPayTax)}</Td>
                   <Td fontWeight="600" color="red.500">{formatCurrency(totalSummary.totalPayTax)}</Td>
                   <Td fontWeight="600" color="red.500">{formatCurrency(totalSummary.deductions || 0)}</Td>
                   <Td fontWeight="700" color="green.600" fontSize="md">{formatCurrency(totalSummary.totalPayNet)}</Td>
                   <Td fontWeight="600" color="blue.600">{formatCurrency(totalSummary.regularNetSalary)}</Td>
                 </Tr>
                 <Tr>
                   <Td colSpan={11} p={0}>
                     <Box bg="blue.50" p={4} borderTop="1px solid" borderColor="blue.200">
                       <VStack spacing={4} align="stretch">
                         <Heading size="sm" color="blue.700" mb={2}>
                           Total Summary - All Fields
                         </Heading>
                         
                         {/* Hours & Rate Information - Collapsible */}
                         <CollapsibleSection 
                           title="Hours & Rate" 
                           isOpen={collapsedSections.hours}
                           onToggle={() => setCollapsedSections(prev => ({ ...prev, hours: !prev.hours }))}
                         >
                           <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={3}>
                             <DetailItem label="Hours Worked" value={totalSummary.totalHoursWorked} />
                             <DetailItem label="Overtime Hours" value={totalSummary.overtimeHours} />
                             <DetailItem label="Missed Hours" value={totalSummary.missedHours} />
                           </SimpleGrid>
                         </CollapsibleSection>

                         {/* Regular Pay Breakdown - Collapsible */}
                         <CollapsibleSection 
                           title="Regular Pay Breakdown" 
                           isOpen={collapsedSections.regularPay}
                           onToggle={() => setCollapsedSections(prev => ({ ...prev, regularPay: !prev.regularPay }))}
                         >
                           <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={3}>
                             <DetailItem label="Regular Pay Gross" value={totalSummary.regularPayGross} />
                             <DetailItem label="Regular Pay Tax" value={totalSummary.regularPayTax} isNegative={true} />
                             <DetailItem label="Regular Pay Net" value={totalSummary.regularPayNet} />
                             <DetailItem label="Regular Net Salary" value={totalSummary.regularNetSalary} />
                           </SimpleGrid>
                         </CollapsibleSection>

                         {/* Total Pay Breakdown - Collapsible */}
                         <CollapsibleSection 
                           title="Total Pay Breakdown" 
                           isOpen={collapsedSections.totalPay}
                           onToggle={() => setCollapsedSections(prev => ({ ...prev, totalPay: !prev.totalPay }))}
                         >
                           <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={3}>
                             <DetailItem label="Total Pay Gross" value={totalSummary.totalPayGross} />
                             <DetailItem label="Total Pay Tax" value={totalSummary.totalPayTax} isNegative={true} />
                             <DetailItem label="Total Pay Net" value={totalSummary.totalPayNet} />
                           </SimpleGrid>
                         </CollapsibleSection>

                         {/* Earnings & Deductions - Collapsible */}
                         <CollapsibleSection 
                           title="Earnings & Deductions" 
                           isOpen={collapsedSections.earnings}
                           onToggle={() => setCollapsedSections(prev => ({ ...prev, earnings: !prev.earnings }))}
                         >
                           <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={3}>
                             <DetailItem label="Overtime Pay" value={totalSummary.overtimePay} />
                             <DetailItem label="Leave Allowance" value={totalSummary.leaveAllowance} />
                             <DetailItem label="Bonus" value={totalSummary.bonus || totalSummary.thirteenthMonthBonus} />
                             <DetailItem label="Missed Hours Deduction" value={totalSummary.missedHoursDeduction} isNegative={true} />
                           </SimpleGrid>
                          <SimpleGrid columns={{ base: 1, md: 2, lg: 2 }} spacing={3} mt={3}>
                            <DetailItem label="Other Deductions" value={totalSummary.deductions} isNegative={true} />
                            <DetailItem 
                              label="Total Deductions" 
                              value={(() => {
                                const deductions = parseFloat(String(totalSummary.deductions || 0).replace(/,/g, '')) || 0;
                                const missedHours = parseFloat(String(totalSummary.missedHoursDeduction || 0).replace(/,/g, '')) || 0;
                                return deductions + missedHours;
                              })()} 
                              isNegative={true} 
                            />
                          </SimpleGrid>
                         </CollapsibleSection>
                         
                         {/* Net Pay Summary */}
                         <Box borderTop="2px solid" borderColor="green.400" pt={4}>
                           <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                             <DetailItem 
                               label="Total Net Pay" 
                               value={totalSummary.totalPayNet} 
                               isNetSalary={true} 
                             />
                             <DetailItem 
                               label="Regular Net Salary" 
                               value={totalSummary.regularNetSalary} 
                               isNetSalary={true} 
                             />
                           </SimpleGrid>
                         </Box>
                       </VStack>
                     </Box>
                   </Td>
                 </Tr>
               </React.Fragment>
             )}
           </Tbody>
        </Table>
      </TableContainer>

      {/* Filter Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Filter Payroll</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack spacing={6} align="stretch">
              {/* Department Filter */}
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="500" color="gray.700">
                  Department
                </FormLabel>
                <Select
                  value={departmentFilter}
                  onChange={(e) => setDepartmentFilter(e.target.value)}
                  placeholder="All Departments"
                  borderRadius="8px"
                  borderColor="gray.200"
                  _focus={{ borderColor: "blue.500" }}
                >
                  {departments.map((dept) => (
                    <option key={dept} value={dept}>
                      {dept}
                    </option>
                  ))}
                </Select>
              </FormControl>

              {/* Branch Filter */}
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="500" color="gray.700">
                  Branch
                </FormLabel>
                <Select
                  value={branchFilter}
                  onChange={(e) => setBranchFilter(e.target.value)}
                  placeholder="All Branches"
                  borderRadius="8px"
                  borderColor="gray.200"
                  _focus={{ borderColor: "blue.500" }}
                >
                  {branches.map((branch) => (
                    <option key={branch} value={branch}>
                      {branch}
                    </option>
                  ))}
                </Select>
              </FormControl>

              {/* Salary Range Filter */}
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="500" color="gray.700">
                  Salary Range
                </FormLabel>
                <Select
                  value={salaryRangeFilter}
                  onChange={(e) => setSalaryRangeFilter(e.target.value)}
                  placeholder="All Salary Ranges"
                  borderRadius="8px"
                  borderColor="gray.200"
                  _focus={{ borderColor: "blue.500" }}
                >
                                   <option value="0-2000">0 - 2,000</option>
                 <option value="2000-5000">2,000 - 5,000</option>
                 <option value="5000-10000">5,000 - 10,000</option>
                 <option value="10000-20000">10,000 - 20,000</option>
                 <option value="20000-999999">20,000+</option>
                </Select>
              </FormControl>

              {/* Action Buttons */}
              <HStack spacing={3} pt={4}>
                <Button
                  colorScheme="blue"
                  variant="solid"
                  onClick={onClose}
                  size="md"
                  borderRadius="8px"
                  flex={1}
                >
                  Apply Filters
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    clearFilters();
                    onClose();
                  }}
                  size="md"
                  borderRadius="8px"
                  flex={1}
                >
                  Clear All
                </Button>
              </HStack>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

// Collapsible Section Component
const CollapsibleSection = ({ title, isOpen, onToggle, children }) => {
  return (
    <Box borderTop="1px solid" borderColor="gray.200" pt={4}>
      <HStack 
        spacing={2} 
        mb={3} 
        cursor="pointer" 
        onClick={onToggle}
        _hover={{ opacity: 0.8 }}
      >
        <Box 
          as={isOpen ? "span" : "span"}
          transform={isOpen ? "rotate(180deg)" : "rotate(0deg)"}
          transition="transform 0.2s"
          display="inline-flex"
        >
          ▼
        </Box>
        <Text fontSize="xs" color="gray.500" fontWeight="600" textTransform="uppercase">
          {title}
        </Text>
      </HStack>
      <Collapse in={isOpen} animateOpacity>
        {children}
      </Collapse>
    </Box>
  );
};

// Detail Item Component
const DetailItem = ({ label, value, isNegative = false, isNetSalary = false }) => {
  const formatValue = () => {
    if (typeof value === 'object' && value !== null) {
      return value.name || value.department_name || JSON.stringify(value);
    }
    
    // Handle string or number values - parse as number for proper formatting
    let numValue;
    if (typeof value === 'string') {
      // Remove commas and parse (handles negative strings like "-4,000.00")
      const cleaned = value.replace(/,/g, '');
      numValue = parseFloat(cleaned) || 0;
    } else if (typeof value === 'number') {
      numValue = value || 0;
    } else {
      numValue = 0;
    }
    
    // Apply negative formatting if needed (for display purposes only)
    // This is for fields like "deductions" and "missedHoursDeduction" which come as positive
    // but should be displayed as negative
    if (isNegative && numValue !== 0 && numValue > 0) {
      numValue = -Math.abs(numValue);
    }
    // If value is already negative (like totalPayNet), keep it as is
    
    // Format as currency using formatCurrency helper
    const formatted = formatCurrency(numValue);
    return formatted;
  };

  const getTextColor = () => {
    if (isNetSalary) return "green.600";
    if (isNegative) return "red.500";
    return "gray.900";
  };

  const getFontWeight = () => {
    if (isNetSalary) return "700";
    if (isNegative) return "500";
    return "600";
  };

  const getFontSize = () => {
    if (isNetSalary) return "md";
    return "sm";
  };

  return (
    <VStack spacing={1} align="start">
      <Text fontSize="xs" color="gray.500" fontWeight="500">
        {label}
      </Text>
      <Text 
        fontSize={getFontSize()} 
        color={getTextColor()} 
        fontWeight={getFontWeight()}
      >
        {formatValue()}
      </Text>
    </VStack>
  );
};

export default PayrollTable;
